import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import { environment } from '@fitness-force/environments';
import { AppErrors, NotFoundError } from '@fitness-force/models';
import type {
  AppointmentBookingV2,
  AppointmentBookingV2Detail,
  ClassBookingV2,
  ClassBookingV2Detail,
  ListClientBookingsV2Params,
  PagedClientBookingsV2Response,
} from '@fitness-force/models';
import { catchError, map, Observable, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ClientBookingV2Service {
  private readonly http = inject(HttpClient);

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  listClassBookings(
    params: ListClientBookingsV2Params,
  ): Observable<PagedClientBookingsV2Response<ClassBookingV2>> {
    const url =
      environment.BASE_URL +
      CONST_URL_SEGEMENTS.SCHEDULING.CLASS.GET_CLASS_BOOKINGS_CLIENT_V2.replace(
        '{tenant_id}',
        params.tenant_id.toString(),
      ).replace('{client_id}', params.client_id.toString());

    const queryParams = new HttpParams()
      .set('PageNumber', params.pageNumber.toString())
      .set('PageSize', params.pageSize.toString());

    return this.http
      .get<PagedClientBookingsV2Response<ClassBookingV2>>(url, {
        headers: this.httpOptions.headers,
        params: queryParams,
      })
      .pipe(
        map((res) => res),
        catchError((error: Response) => this.handleError(error)),
      );
  }

  getClassBookingDetail(
    tenantId: number,
    clientId: number,
    bookingId: number,
  ): Observable<ClassBookingV2Detail> {
    const url =
      environment.BASE_URL +
      CONST_URL_SEGEMENTS.SCHEDULING.CLASS.GET_CLASS_BOOKING_DETAIL_V2.replace(
        '{tenant_id}',
        tenantId.toString(),
      )
        .replace('{client_id}', clientId.toString())
        .replace('{booking_id}', bookingId.toString());

    return this.http
      .get<ClassBookingV2Detail>(url, { headers: this.httpOptions.headers })
      .pipe(
        map((res) => res),
        catchError((error: Response) => this.handleError(error)),
      );
  }

  listAppointmentBookings(
    params: ListClientBookingsV2Params,
  ): Observable<PagedClientBookingsV2Response<AppointmentBookingV2>> {
    const url =
      environment.BASE_URL +
      CONST_URL_SEGEMENTS.SCHEDULING.APPOINTMENT.GET_APPOINTMENT_BOOKINGS_CLIENT_V2.replace(
        '{tenant_id}',
        params.tenant_id.toString(),
      ).replace('{client_id}', params.client_id.toString());

    const queryParams = new HttpParams()
      .set('PageNumber', params.pageNumber.toString())
      .set('PageSize', params.pageSize.toString());

    return this.http
      .get<PagedClientBookingsV2Response<AppointmentBookingV2>>(url, {
        headers: this.httpOptions.headers,
        params: queryParams,
      })
      .pipe(
        map((res) => res),
        catchError((error: Response) => this.handleError(error)),
      );
  }

  getAppointmentBookingDetail(
    tenantId: number,
    clientId: number,
    appointmentId: number,
  ): Observable<AppointmentBookingV2Detail> {
    const url =
      environment.BASE_URL +
      CONST_URL_SEGEMENTS.SCHEDULING.APPOINTMENT.GET_APPOINTMENT_BOOKING_DETAIL_V2.replace(
        '{tenant_id}',
        tenantId.toString(),
      )
        .replace('{client_id}', clientId.toString())
        .replace('{appointment_id}', appointmentId.toString());

    return this.http
      .get<AppointmentBookingV2Detail>(url, {
        headers: this.httpOptions.headers,
      })
      .pipe(
        map((res) => res),
        catchError((error: Response) => this.handleError(error)),
      );
  }

  private handleError(error: Response): Observable<never> {
    if (error.status === 404) {
      return throwError(() => new NotFoundError(error));
    }
    return throwError(() => new AppErrors(error));
  }
}
